import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import dayjs from "dayjs"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getSelectedRegionId} from "../../utils/common"
import httpService from "../../utils/httpService"
import {setToastr} from "../App/appSlice"

export interface DvsaFee {
    dealerID: number
    dealerName: string
    dvsaFee: number | null
    defaultDate: string | null
    dayFee1: number | null
    effectiveDate1: string
    updateBy: string
    updateDate: Date | null
    dayFee2: number
    effectiveDate2: string
    dayFee3: number | null
    effectiveDate3: string
    dayFee4: number | null
    effectiveDate4: string
    dayFee5: number | null
    effectiveDate5: string
    dayFee6: number | null
    effectiveDate6: string
}

export interface DvsaFeeDefault {
    dayFee: number
    dvsaFeeID: number
    effectiveDate: Date | null
    updatedBy: string
    updatedDate: Date | null
    isException: boolean
}

export const dvsaDefaultInit = () =>
    ({
        dayFee: 0,
        dvsaFeeID: 0,
        effectiveDate: null,
        updatedBy: "",
        updatedDate: null,
        isException: false,
    } as DvsaFeeDefault)

type DvsaFeesState = {
    dvsaFees: DvsaFee[]
    dvsaModalOpen: boolean
    dvsaModalAddEdit: boolean
    selectedDvsaID: number
    showLoader: boolean
    dvsaFeeGirdLoader: boolean
    dvsaFeeLoader: boolean
    dvsaFeeDefaults: DvsaFeeDefault[]
    selectedDVSAdefault: DvsaFeeDefault
    selectedDealer: string
}

const initialState: DvsaFeesState = {
    dvsaFees: [],
    dvsaModalOpen: false,
    dvsaModalAddEdit: false,
    selectedDvsaID: 0,
    showLoader: false,
    dvsaFeeDefaults: [],
    dvsaFeeGirdLoader: false,
    dvsaFeeLoader: false,
    selectedDVSAdefault: dvsaDefaultInit(),
    selectedDealer: "",
}

const feesSlice = createSlice({
    name: "dvsaFees",
    initialState,
    reducers: {
        setDvsaFees(state, action: PayloadAction<DvsaFee[]>) {
            state.dvsaFees = action.payload
        },
        showLoader(state, action: PayloadAction<boolean>) {
            state.showLoader = action.payload
        },
        setDvsaGridLoader(state, action: PayloadAction<boolean>) {
            state.dvsaFeeGirdLoader = action.payload
        },
        dvsaToggleAddEditModal(state, action: PayloadAction<boolean>) {
            state.dvsaModalOpen = action.payload
        },
        toggleDVSAModalAddEdit(state, action: PayloadAction<boolean>) {
            state.dvsaModalAddEdit = action.payload
        },
        setDsvaFeeSelectedId(state, action: PayloadAction<number>) {
            state.selectedDvsaID = action.payload
        },
        setDVSAFeeDefaults(state, action: PayloadAction<DvsaFeeDefault[]>) {
            state.dvsaFeeDefaults = action.payload
        },
        setSelectedExpetion(state, action: PayloadAction<DvsaFeeDefault>) {
            state.selectedDVSAdefault = action.payload
        },
        setSelectedDvsaFee(state, action: PayloadAction<string>) {
            state.selectedDealer = action.payload
        },
        setDVSAFeeLoader(state, action: PayloadAction<boolean>) {
            state.dvsaFeeLoader = action.payload
        },
    },
})

export const {
    setDvsaFees,
    showLoader,
    setDvsaGridLoader,
    dvsaToggleAddEditModal,
    toggleDVSAModalAddEdit,
    setDsvaFeeSelectedId,
    setDVSAFeeDefaults,
    setSelectedExpetion,
    setSelectedDvsaFee,
    setDVSAFeeLoader,
} = feesSlice.actions

export default feesSlice.reducer

// #region Thunks
export const fetchDvsaFees = (): AppThunk => (dispatch: any, getState) => {
    dispatch(showLoader(true))
    httpService(getAccessToken(getState()))
        .get(`dvsa/${getSelectedRegionId(getState())}/dvsafees`)
        .then(res => {
            dispatch(showLoader(false))
            dispatch(setDvsaFees(res.data))
        })
        .catch(error => {
            dispatch(showLoader(false))
            dispatch(setDvsaFees([]))
        })
}

export const fetchDvsaFeeById =
    (selectedDvsaId: number): AppThunk =>
    (dispatch: any, getState) => {
        const formatAsDate = (dateColumn: Date | null) => (dateColumn !== null ? new Date(dateColumn) : null)

        dispatch(setDvsaGridLoader(true))
        httpService(getAccessToken(getState()))
            .get(`dvsa/${getSelectedRegionId(getState())}/dealers/${selectedDvsaId}/dvsafees`)
            .then(res => {
                dispatch(
                    setDVSAFeeDefaults(
                        res.data.map((d: any) => ({
                            ...d,
                            effectiveDate: formatAsDate(d.effectiveDate),
                            updatedDate: dayjs(d.updatedDate).format("DD/MM/YYYY HH:mm"),
                        })),
                    ),
                )
                dispatch(setDvsaGridLoader(false))
            })
            .catch(error => {
                dispatch(setDVSAFeeDefaults([]))
                dispatch(setDvsaGridLoader(false))
            })
    }
export const addDvsaFeeById =
    (selectedDvsaId: number, dvsaFeeDetails: any): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setDVSAFeeLoader(true))
        httpService(getAccessToken(getState()))
            .post(`dvsa/${getSelectedRegionId(getState())}/dealers/${selectedDvsaId}/dvsafees`, dvsaFeeDetails)
            .then(res => {
                dispatch(fetchDvsaFees())
                dispatch(fetchDvsaFeeById(selectedDvsaId))
                dispatch(toggleDVSAModalAddEdit(false))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Successfully added",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
            .finally(() => dispatch(setDVSAFeeLoader(false)))
    }

export const editDvsaFeeExpection =
    (selectedDvsaId: number, dvsaFeeDetails: DvsaFeeDefault): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setDVSAFeeLoader(true))
        httpService(getAccessToken(getState()))
            .put(`dvsa/${getSelectedRegionId(getState())}/dealers/${selectedDvsaId}/dvsafees`, dvsaFeeDetails)
            .then(res => {
                dispatch(fetchDvsaFees())
                dispatch(toggleDVSAModalAddEdit(false))
                dispatch(fetchDvsaFeeById(selectedDvsaId))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Successfully edited",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
            })
            .catch(error => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
            .finally(() => dispatch(setDVSAFeeLoader(false)))
    }
